import { FC, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { SmallButton } from '@/shared/SmallButton';
import { Message } from '@/types/chat';
import { CloseIcon, DownloadIcon, RefreshIcon } from '../../shared/icons';
import { generatePdf } from '@/components/PdfExport';
import Markdown from '@/components/Markdown/Markdown';
import { MessageTextComposer } from './MessageTextComposer';
import { BlockRenderer } from '@/components/ReportBlocks';
import type { ReportBlock } from '@/types/blocks';

interface MessageProps {
  message: Message;
  isLoading?: boolean;
  isEditing?: boolean;
  onEditStart?: () => void;
  onEditEnd?: () => void;
  onRegenerate?: (text: string) => void;
  onCancel?: () => void;
  onSubmitPrompt?: (text: string) => void;
  isLastMessage?: boolean;
}

export const MessageComponent: FC<MessageProps> = ({
  message,
  isLoading,
  isEditing,
  onEditEnd,
  onEditStart,
  onRegenerate,
  onCancel,
  onSubmitPrompt,
  isLastMessage = false,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [isTitleExpanded, setIsTitleExpanded] = useState(false);
  const [isTitleClamped, setIsTitleClamped] = useState(false);
  const [exporting, setExporting] = useState(false);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleExportPdf = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await generatePdf(message, contentRef);
    } catch (error) {
      console.error('PDF export failed:', error);
    } finally {
      setExporting(false);
    }
  };

  const handleSubmit = (value: string) => {
    if (!value) {
      return;
    }
    handleClose();
    setSubmitting(true);
    if (onRegenerate) {
      onRegenerate(value);
    }
  };

  const handleRegenerateClick = () => {
    handleSubmit(message.title || '');
  };

  const handleFocus = () => {
    setIsTitleExpanded(false);
    onEditStart?.();
  };

  const handleClose = () => {
    onEditEnd?.();
  };

  const handleCancel = () => {
    onCancel?.();
  };

  const checkClamped = useCallback(() => {
    const el = titleRef.current;
    if (!el) return;
    setIsTitleClamped(el.scrollHeight > el.clientHeight);
  }, []);

  useLayoutEffect(() => {
    if (isTitleExpanded) return;
    checkClamped();
  }, [message.title, isTitleExpanded, checkClamped]);

  return (
    <div className="flex flex-col bg-white dark:bg-white-dark rounded-[24px] border border-peach-1 dark:border-grey-plain-dark w-full overflow-hidden">
      {message.title && (
        <div
          className="px-4 pb-4 pt-4 md:px-6 md:pt-6"
        >
          {!isEditing || isLoading ? (
            <div
              className="min-h-[72px] rounded-[16px] bg-white p-4 text-left transition-colors hover:bg-grey-light dark:bg-white-dark dark:hover:bg-grey-dark"
              onClick={isLoading ? undefined : handleFocus}
              role={isLoading ? undefined : "button"}
              tabIndex={isLoading ? -1 : 0}
              onKeyDown={(event) => {
                if (isLoading) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleFocus();
                }
              }}
            >
              <div
                ref={titleRef}
                className="break-words whitespace-pre-wrap text-md font-medium text-grey-black dark:text-grey-main-dark"
                style={
                  isTitleExpanded
                    ? undefined
                    : {
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }
                }
              >
                {message.title}
              </div>
              {(isTitleClamped || isTitleExpanded) && (
                <button
                  type="button"
                  className="mt-2 text-sm text-grey-medium transition-colors hover:text-grey-black dark:hover:text-grey-main-dark"
                  onClick={(event) => {
                    event.stopPropagation();
                    setIsTitleExpanded((prev) => !prev);
                  }}
                >
                  {isTitleExpanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
          ) : (
            <MessageTextComposer
              messageTitle={message.title || ''}
              submitting={submitting}
              editing={!!isEditing}
              onFocus={handleFocus}
              onClose={handleClose}
              onSubmit={handleSubmit}
            />
          )}
        </div>
      )}
      <div className="relative overflow-clip w-full max-w-[780px] mx-auto h-[1px] bg-peach-1 dark:bg-grey-plain-dark">
        {isLoading && (
          <div className="absolute w-[50%] h-[1px] bg-grey-black dark:bg-grey-plain animate-load-message"></div>
        )}
      </div>
      <div ref={contentRef} className="flex flex-col gap-4 px-4 pb-4 pt-4 md:px-10 md:pb-10 md:pt-8">
        {!isLoading && message.structured?.blocks?.length ? (
          <BlockRenderer blocks={message.structured.blocks as ReportBlock[]} onSubmitPrompt={onSubmitPrompt} isLastMessage={isLastMessage} />
        ) : (
          <Markdown>
            {isLoading
              ? (message.content ?? '').trim().length > 0
                ? message.content
                : 'Analysing news, creating the scenario, assesing the influence and calculating the most optimal proactive actions... Will take approximately 15 mins.'
              : message.content ?? ''}
          </Markdown>
        )}
      </div>
      <div className="px-4 pb-4 md:px-10 md:pb-10">
        {!isLoading ? (
          <div className="flex flex-wrap gap-2">
            <SmallButton
              title="Regenerate"
              leftIcon={<RefreshIcon />}
              onClick={handleRegenerateClick}
            />
            <SmallButton
              title={exporting ? 'Exporting...' : 'PDF'}
              leftIcon={<DownloadIcon />}
              onClick={handleExportPdf}
            />
          </div>
        ) : (
          <SmallButton title="Close" onClick={handleCancel} leftIcon={<CloseIcon />} />
        )}
      </div>
    </div>
  );
};
